import { useEffect, useRef, useState } from 'react'
import { Search, X } from 'lucide-react'
import { InFilterBar } from './filterContext'
import { kit } from './strings'
import './FilterBar.css'

/* How long typing has to pause before the search is applied. */
const SEARCH_DELAY_MS = 250

/**
 * THE FIELD OWNS WHAT IS TYPED. Most screens keep the search in the address (`?q=`), and the
 * field used to show that value directly: every keystroke went through the router and came
 * back a render later, so a second key pressed before then was typed into the stale value and
 * lost — "bayan88" typed quickly left "b8" (Taiga #1519), and each key fired its own request.
 * The text is kept here and handed on once typing pauses; a change from outside (a clear
 * elsewhere, Back) still replaces it.
 */
function SearchField({ search }) {
  const value = search.value ?? ''
  const [draft, setDraft] = useState(value)
  const sent = useRef(value)
  const onChange = useRef(search.onChange)
  useEffect(() => {
    onChange.current = search.onChange
  })

  useEffect(() => {
    if (value === sent.current) return
    sent.current = value
    setDraft(value)
  }, [value])

  useEffect(() => {
    if (draft === sent.current) return undefined
    const timer = setTimeout(() => {
      sent.current = draft
      onChange.current(draft)
    }, SEARCH_DELAY_MS)
    return () => clearTimeout(timer)
  }, [draft])

  const clear = () => {
    sent.current = ''
    setDraft('')
    onChange.current('')
  }

  return (
    <div className="filter-bar__search">
      <Search className="filter-bar__search-icon" size={16} strokeWidth={1.75} aria-hidden="true" />
      <input
        className="filter-bar__search-input"
        type="search"
        value={draft}
        placeholder={search.placeholder}
        /* A placeholder is not a name: a screen reader announced "edit text" and nothing
           else on every list that used this bar. */
        aria-label={search.label || search.placeholder || kit.search}
        onChange={(event) => setDraft(event.target.value)}
        autoComplete="off"
      />
      {draft && (
        <button
          type="button"
          className="filter-bar__search-clear"
          onClick={clear}
          aria-label={kit.clearSearch}
        >
          <X size={13} strokeWidth={2.5} />
        </button>
      )}
    </div>
  )
}

/**
 * The control strip above a list.
 *
 * The legacy hides filtering behind a `بحث` dropdown: you pick a department, the
 * menu closes, and nothing on screen says the list is now filtered. Here the
 * controls are inline and every applied filter stays on the page as a chip you can
 * take off one at a time.
 *
 * @param {{value: string, onChange: (value: string) => void, placeholder?: string, label?: string}} [search]
 *        `label` names the field for assistive technology; the placeholder does when omitted.
 * @param {{id: string, label: string, value: string, onRemove: () => void}[]} [chips]
 * @param {() => void} [onReset]   drawn as a "clear all" pill once a chip is up
 * @param {import('react').ReactNode} [children]  the inline filter controls
 * @param {import('react').ReactNode} [actions]   end-side actions (export, density…)
 * @param {string} [count]         result summary, parked at the inline-end of the
 *                                 chip row so "what is applied" and "what it left"
 *                                 read as one statement
 */
export default function FilterBar({
  search,
  children,
  chips = [],
  onReset,
  resetLabel = kit.clearAll,
  actions,
  count,
}) {
  const hasMeta = chips.length > 0 || count

  return (
    <div className="filter-bar" data-testid="filter-bar">
      <div className="filter-bar__row">
        {search && <SearchField search={search} />}

        {/* A select in here names its filter in the trigger once it has a value. */}
        {children && (
          <div className="filter-bar__controls">
            <InFilterBar.Provider value>{children}</InFilterBar.Provider>
          </div>
        )}

        {actions && <div className="filter-bar__actions">{actions}</div>}
      </div>

      {hasMeta && (
        <div className="filter-bar__meta">
          {chips.length > 0 && (
            <div className="filter-bar__chips">
              {chips.map((chip) => (
                <span
                  key={chip.id}
                  className="filter-chip"
                  title={`${chip.label}: ${chip.value}`}
                  data-testid="filter-chip"
                  data-filter-key={chip.id}
                >
                  <span className="filter-chip__label">
                    <bdi>{chip.label}</bdi>
                    <span className="filter-chip__sep" aria-hidden="true">
                      ·
                    </span>
                    <span className="filter-chip__value">
                      <bdi>{chip.value}</bdi>
                    </span>
                  </span>
                  <button
                    type="button"
                    className="filter-chip__x"
                    onClick={chip.onRemove}
                    aria-label={`${kit.remove} ${chip.label}`}
                  >
                    <X size={12} strokeWidth={2.5} />
                  </button>
                </span>
              ))}

              {onReset && (
                <button type="button" className="filter-bar__reset" onClick={onReset} data-testid="filter-clear">
                  <X size={12} strokeWidth={2.5} aria-hidden="true" />
                  <bdi>{resetLabel}</bdi>
                </button>
              )}
            </div>
          )}

          {count && <span className="filter-bar__count">{count}</span>}
        </div>
      )}
    </div>
  )
}
